/**
 * Publish Version Service — types + API methods for entity version history.
 *
 * Supports Content, Module, and Program entity types.
 * Backend reference: docs/PUBLISH-VERSIONING.md
 */

import { sharedApiClient } from './client';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

/** One row in the version history list. */
export interface VersionListItem {
    id: string;
    version: number;
    label: string;
    notes: string;
    created_by: string;
    created_at: string;
    size_bytes: number | null;
    is_current: boolean;
    diff_summary: {
        added: string[];
        modified: string[];
        deleted: string[];
    } | null;
}

export interface VersionListResponse {
    results: VersionListItem[];
    count: number;
    page: number;
    page_size: number;
    total_pages: number;
}

/** Full version detail returned by GET …/{v}/. */
export interface VersionDetail extends VersionListItem {
    snapshot: Record<string, unknown> | null;
}

export interface VersionPatchPayload {
    label?: string;
    notes?: string;
}

/** Response from POST …/{v}/restore/. */
export interface RestoreResponse {
    entity_id: string;
    version: number;
    skipped_module_ids?: string[];
}

/* ------------------------------------------------------------------ */
/*  Service                                                            */
/* ------------------------------------------------------------------ */

export type VersionEntityType = 'content' | 'module' | 'program';

function versionBase(type: VersionEntityType, entityId: string): string {
    if (type === 'content') return `/content/${entityId}/publish-versions`;
    if (type === 'module') return `/program/modules/${entityId}/versions`;
    return `/program/programs/${entityId}/versions`;
}

function withTenant(qs: string, tenantId?: string): string {
    if (!tenantId) return qs;
    const sep = qs.includes('?') ? '&' : '?';
    return `${qs}${sep}tenant_id=${encodeURIComponent(tenantId)}`;
}

export const versionService = {
    list(type: VersionEntityType, entityId: string, page = 1, pageSize = 20, tenantId?: string) {
        const base = versionBase(type, entityId);
        const url = withTenant(`${base}/?page=${page}&page_size=${pageSize}`, tenantId);
        return sharedApiClient.get<VersionListResponse>(url);
    },

    getDetail(type: VersionEntityType, entityId: string, version: number, tenantId?: string) {
        const url = withTenant(`${versionBase(type, entityId)}/${version}/`, tenantId);
        return sharedApiClient.get<VersionDetail>(url);
    },

    patch(
        type: VersionEntityType,
        entityId: string,
        version: number,
        payload: VersionPatchPayload,
        tenantId?: string,
    ) {
        const url = withTenant(`${versionBase(type, entityId)}/${version}/`, tenantId);
        return sharedApiClient.patch<VersionDetail>(url, payload);
    },

    restore(type: VersionEntityType, entityId: string, version: number, tenantId?: string) {
        const url = withTenant(`${versionBase(type, entityId)}/${version}/restore/`, tenantId);
        return sharedApiClient.post<RestoreResponse>(url, {});
    },
};
